import { useEffect } from 'react';
import {
  FieldExtensionComponentProps,
  useTemplateSecrets,
} from '@backstage/plugin-scaffolder-react';
import { Alert } from '@backstage/ui';
import { useGitHubToken } from './useGitHubToken';

const SECRET_NAME = 'USER_OAUTH_TOKEN';
const GITHUB_TOKEN_AVAILABLE_VALUE = 'github-token-available';

type GitHubUserTokenUiOptions = {
  // GitHub OAuth scopes to request for the resulting user token. The template
  // author is responsible for choosing the minimum scope set required by the
  // template's downstream steps (principle of least privilege). See
  // https://docs.github.com/en/apps/oauth-apps/building-oauth-apps/scopes-for-oauth-apps
  scopes: string[];
};

function isNonEmptyStringArray(value: unknown): value is string[] {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every(s => typeof s === 'string' && s.length > 0)
  );
}

// This is a custom field extension for the Scaffolder plugin that fetches a
// GitHub user token and stores it in the template secrets context under the
// 'USER_OAUTH_TOKEN' key. This field extension has no UI.
//
// The template must declare the required OAuth scopes via `ui:options.scopes`,
// for example:
//
//   gitHubTokenProvider:
//     type: string
//     ui:field: GitHubUserToken
//     ui:options:
//       scopes:
//         - repo
export const GitHubUserToken = (
  props: FieldExtensionComponentProps<string, GitHubUserTokenUiOptions>,
) => {
  const { setSecrets } = useTemplateSecrets();
  const { onChange } = props;
  const rawScopes = props.uiSchema?.['ui:options']?.scopes;
  const scopesAreWellFormed = isNonEmptyStringArray(rawScopes);

  const { token } = useGitHubToken(scopesAreWellFormed ? rawScopes : []);

  useEffect(() => {
    if (scopesAreWellFormed && token) {
      setSecrets({ [SECRET_NAME]: token });
      // Since the githubTokenProvider is required, you need to trigger the onChange
      // event with any value.  Otherwise validation will fail when you try to go
      // to the next page/step.  So you need something here, but you can't
      // post the token itself.
      onChange(GITHUB_TOKEN_AVAILABLE_VALUE);
    }
  }, [scopesAreWellFormed, token, setSecrets, onChange]);

  if (!scopesAreWellFormed) {
    return (
      <Alert
        status="danger"
        title="GitHubUserToken field extension is misconfigured"
        description="`ui:options.scopes` must be a non-empty array of GitHub OAuth scope strings."
        icon
      />
    );
  }

  // This field extension does not render any UI when correctly configured
  return <></>;
};
